import { Link, useParams } from 'react-router';
import { api, type Invoice } from '../api';
import { DownloadIcon } from '../components/icons';
import { ErrorNote, formatDay, PageHeader } from '../components/ui';
import { useLoad } from '../hooks';
import { rupees } from './Billing';

const amount = (paise: number) =>
  (paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A GST tax invoice, laid out to print (or save as PDF) on A4. */
export function InvoiceDetail() {
  const { id = '' } = useParams();
  const { data: inv, error } = useLoad(
    () => api<Invoice>('GET', `/billing/invoices/${encodeURIComponent(id)}`),
    [id],
  );

  return (
    <>
      <PageHeader
        crumbs={
          <>
            <Link to="/billing">Billing</Link>
            <span>/</span>
            <span>Invoice</span>
          </>
        }
        title={inv ? inv.number : 'Invoice'}
        actions={
          inv ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.print()}>
              <DownloadIcon />
              Print or save as PDF
            </button>
          ) : null
        }
      />
      <ErrorNote error={error} />
      {inv ? (
        <article className="invoice">
          <header className="invoice-head">
            <div>
              <h2>Tax invoice</h2>
              <p>
                <strong>{inv.seller.legalName}</strong>
                <br />
                <span style={{ whiteSpace: 'pre-line' }}>{inv.seller.address}</span>
                <br />
                GSTIN {inv.seller.gstin}
                <br />
                State: {inv.sellerState} ({inv.seller.stateCode})
              </p>
            </div>
            <dl>
              <dt>Invoice number</dt>
              <dd>{inv.number}</dd>
              <dt>Invoice date</dt>
              <dd>{formatDay(inv.issuedAt)}</dd>
              <dt>Payment</dt>
              <dd>{inv.paymentId}</dd>
            </dl>
          </header>

          <section className="invoice-parties">
            <div>
              <h3>Billed to</h3>
              <p>
                <strong>{inv.buyer.legalName}</strong>
                <br />
                <span style={{ whiteSpace: 'pre-line' }}>{inv.buyer.address}</span>
                {inv.buyer.gstin ? (
                  <>
                    <br />
                    GSTIN {inv.buyer.gstin}
                  </>
                ) : null}
              </p>
            </div>
            <div>
              <h3>Place of supply</h3>
              <p>
                {inv.buyerState} ({inv.buyer.stateCode})
              </p>
            </div>
          </section>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>SAC</th>
                <th className="num">Taxable value (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Vaartix {inv.planName} plan, email sending service
                  {inv.periodStart && inv.periodEnd ? (
                    <span className="period">
                      {formatDay(inv.periodStart)} to {formatDay(inv.periodEnd)}
                    </span>
                  ) : null}
                </td>
                <td>{inv.seller.sac}</td>
                <td className="num">{amount(inv.taxablePaise)}</td>
              </tr>
            </tbody>
            <tfoot>
              {inv.igstPaise ? (
                <tr>
                  <td colSpan={2}>IGST @ 18%</td>
                  <td className="num">{amount(inv.igstPaise)}</td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td colSpan={2}>CGST @ 9%</td>
                    <td className="num">{amount(inv.cgstPaise)}</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>SGST @ 9%</td>
                    <td className="num">{amount(inv.sgstPaise)}</td>
                  </tr>
                </>
              )}
              <tr className="total">
                <td colSpan={2}>Total (paid)</td>
                <td className="num">{rupees(inv.totalPaise)}</td>
              </tr>
            </tfoot>
          </table>
          <p className="invoice-fine">
            Tax is not payable on reverse charge. This is a computer-generated invoice and needs no
            signature. Questions: support@vaartix.com
          </p>
        </article>
      ) : null}
    </>
  );
}
